import { useEffect, useState } from "react"
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Select,
  Switch,
  Table,
  TimePicker,
  message,
} from "antd"
import { PlusOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import apiClient from "../../../Utils/apiClient"

const COUNTRIES = [
  { value: "", label: "All countries" },
  { value: "EMEA", label: "Region: EMEA" },
  { value: "NLAM", label: "Region: NLAM (NL + Americas)" },
  { value: "APAC", label: "Region: APAC" },
  { value: "IN", label: "India" },
  { value: "AE", label: "United Arab Emirates" },
  { value: "NL", label: "Netherlands" },
  { value: "GB", label: "United Kingdom" },
  { value: "IE", label: "Ireland" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "BE", label: "Belgium" },
  { value: "ES", label: "Spain" },
  { value: "PT", label: "Portugal" },
  { value: "IT", label: "Italy" },
  { value: "PL", label: "Poland" },
  { value: "SE", label: "Sweden" },
  { value: "ZA", label: "South Africa" },
  { value: "SA", label: "Saudi Arabia" },
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "MX", label: "Mexico" },
  { value: "BR", label: "Brazil" },
  { value: "AR", label: "Argentina" },
  { value: "CL", label: "Chile" },
  { value: "CO", label: "Colombia" },
  { value: "SG", label: "Singapore" },
]

const IN_STATES = [
  "Andhra Pradesh",
  "Delhi",
  "Gujarat",
  "Karnataka",
  "Kerala",
  "Maharashtra",
  "Tamil Nadu",
  "Telangana",
  "Uttar Pradesh",
  "West Bengal",
]

const AE_STATES = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Ras Al Khaimah",
  "Fujairah",
  "Umm Al Quwain",
]

const NL_STATES = [
  "North Holland",
  "South Holland",
  "Utrecht",
  "North Brabant",
  "Gelderland",
  "Overijssel",
  "Limburg",
  "Flevoland",
  "Friesland",
  "Groningen",
  "Drenthe",
  "Zeeland",
]

const US_STATES = [
  "California",
  "Texas",
  "New York",
  "Florida",
  "Illinois",
  "Washington",
  "Massachusetts",
  "Georgia",
  "North Carolina",
  "Virginia",
]

const toHm = (value, fallback = "09:00") => {
  if (!value) return fallback
  if (typeof value === "string") return value.slice(0, 5)
  if (typeof value.format === "function") return value.format("HH:mm")
  return fallback
}

const InactivityTenantSettings = () => {
  const [form] = Form.useForm()
  const [holidayForm] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [settings, setSettings] = useState(null)
  const holidayCountry = Form.useWatch("country", holidayForm)

  const applySettings = (result) => {
    setSettings(result)
    form.setFieldsValue({
      enabled: result.enabled !== false,
      workStart: dayjs(result.workStart || "09:00", "HH:mm"),
      workEnd: dayjs(result.workEnd || "18:00", "HH:mm"),
      skipWeekends: result.skipWeekends !== false,
      skipHolidays: result.skipHolidays !== false,
      useSharedHolidays: result.useSharedHolidays !== false,
      hourlyEnabled: result.hourlyEnabled !== false,
      noonEnabled: result.noonEnabled !== false,
      digest48hEnabled: result.digest48hEnabled !== false,
    })
  }

  const payloadFromForm = (values, holidays) => ({
    enabled: Boolean(values.enabled),
    workStart: toHm(values.workStart, "09:00"),
    workEnd: toHm(values.workEnd, "18:00"),
    skipWeekends: Boolean(values.skipWeekends),
    skipHolidays: Boolean(values.skipHolidays),
    useSharedHolidays: Boolean(values.useSharedHolidays),
    hourlyEnabled: Boolean(values.hourlyEnabled),
    noonEnabled: Boolean(values.noonEnabled),
    digest48hEnabled: Boolean(values.digest48hEnabled),
    holidays: holidays || settings?.holidays || [],
  })

  const load = async () => {
    setLoading(true)
    try {
      const res = await apiClient("settings/inactivity-notifications", "GET")
      if (res?.success) applySettings(res.result)
    } catch (err) {
      message.error("Could not load inactivity settings")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const save = async () => {
    const values = await form.validateFields()
    setLoading(true)
    try {
      const res = await apiClient(
        "settings/inactivity-notifications",
        "PUT",
        payloadFromForm(values)
      )
      if (res?.success) {
        message.success(res.message)
        applySettings(res.result)
      } else {
        message.error(res?.message || "Could not save")
      }
    } catch (err) {
      message.error(err.message || "Could not save inactivity settings")
    } finally {
      setLoading(false)
    }
  }

  const saveHolidays = async (holidays) => {
    setLoading(true)
    try {
      const values = form.getFieldsValue()
      const res = await apiClient(
        "settings/inactivity-notifications",
        "PUT",
        payloadFromForm(values, holidays)
      )
      if (res?.success) {
        applySettings(res.result)
        return true
      }
      message.error(res?.message || "Could not update holidays")
      return false
    } catch (err) {
      message.error(err.message || "Could not update holidays")
      return false
    } finally {
      setLoading(false)
    }
  }

  const addHoliday = async () => {
    const values = await holidayForm.validateFields()
    const date = values.date ? values.date.format("YYYY-MM-DD") : ""
    if (!date) {
      message.warning("Pick a holiday date")
      return
    }
    const next = [
      ...(settings?.holidays || []),
      {
        date,
        name: values.name || "",
        country: values.country || "",
        state: values.state || "",
        recurring: Boolean(values.recurring),
      },
    ]
    const ok = await saveHolidays(next)
    if (ok) {
      holidayForm.resetFields()
      message.success("Holiday added")
    }
  }

  const removeHoliday = async (index) => {
    const next = (settings?.holidays || []).filter((_, i) => i !== index)
    const ok = await saveHolidays(next)
    if (ok) message.success("Holiday removed")
  }

  const stateOptions =
    holidayCountry === "IN"
      ? IN_STATES
      : holidayCountry === "AE"
      ? AE_STATES
      : holidayCountry === "NL"
      ? NL_STATES
      : holidayCountry === "US"
      ? US_STATES
      : []

  return (
    <Card
      className="ops-card mb-3"
      loading={loading && !settings}
      title="Device inactivity email"
      extra={
        <Button type="primary" onClick={save} loading={loading}>
          Save schedule
        </Button>
      }
    >
      <p className="user-activity__muted" style={{ marginTop: 0 }}>
        Tenant defaults for inactivity alerts. Work hours use each device&apos;s
        local clock. Holidays match the device location country and state. You
        can still turn a user or a device off in the tables below.
      </p>
      <Form form={form} layout="vertical">
        <Form.Item
          name="enabled"
          label="Send inactivity emails"
          extra="Turns hourly, noon, and 48-hour alerts off for the whole tenant"
          valuePropName="checked"
        >
          <Switch checkedChildren="On" unCheckedChildren="Off" />
        </Form.Item>
        <div className="form-inline-fields">
          <Form.Item
            name="workStart"
            label="Work starts"
            extra="No alerts before this time"
          >
            <TimePicker format="HH:mm" className="w-100" />
          </Form.Item>
          <Form.Item
            name="workEnd"
            label="Work ends"
            extra="No alerts after this time"
          >
            <TimePicker format="HH:mm" className="w-100" />
          </Form.Item>
        </div>
        <Form.Item name="skipWeekends" label="Skip Saturday and Sunday" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="skipHolidays" label="Skip listed holidays" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          name="useSharedHolidays"
          label="Also use shutdown-reminder holidays"
          extra="Country and state holidays already listed above are reused"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item name="hourlyEnabled" label="Hourly 2–8 hour alerts" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="noonEnabled" label="Noon no-activity alert" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          name="digest48hEnabled"
          label="48-hour admin digest"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>

      <h4>Extra inactivity holidays</h4>
      <p className="user-activity__muted">
        Leave country empty to apply everywhere. Add a state to limit the
        holiday to that region (for example Karnataka or Dubai).
      </p>
      <Form form={holidayForm} layout="inline" className="mb-3" style={{ rowGap: 12 }}>
        <Form.Item name="date" rules={[{ required: true, message: "Date" }]}>
          <DatePicker />
        </Form.Item>
        <Form.Item name="name">
          <Input placeholder="Holiday name" />
        </Form.Item>
        <Form.Item name="country" initialValue="">
          <Select style={{ width: 200 }} options={COUNTRIES} />
        </Form.Item>
        <Form.Item name="state">
          {stateOptions.length ? (
            <Select
              allowClear
              showSearch
              style={{ width: 180 }}
              placeholder="State / emirate"
              options={stateOptions.map((item) => ({ value: item, label: item }))}
            />
          ) : (
            <Input placeholder="State (optional)" />
          )}
        </Form.Item>
        <Form.Item name="recurring" valuePropName="checked">
          <Switch checkedChildren="Every year" unCheckedChildren="This year" />
        </Form.Item>
        <Form.Item>
          <Button icon={<PlusOutlined />} onClick={addHoliday}>
            Add holiday
          </Button>
        </Form.Item>
      </Form>
      <Table
        size="small"
        rowKey={(row, index) => row._id || `${row.date}-${index}`}
        pagination={false}
        dataSource={settings?.holidays || []}
        locale={{ emptyText: "No extra holidays. Shared shutdown holidays can still apply." }}
        columns={[
          { title: "Date", dataIndex: "date", width: 130 },
          { title: "Name", dataIndex: "name", render: (value) => value || "—" },
          {
            title: "Country",
            dataIndex: "country",
            width: 110,
            render: (value) => value || "All",
          },
          {
            title: "State",
            dataIndex: "state",
            render: (value) => value || "All",
          },
          {
            title: "Repeats",
            dataIndex: "recurring",
            width: 90,
            render: (value) => (value ? "Yearly" : "Once"),
          },
          {
            title: "",
            key: "remove",
            width: 90,
            render: (_, __, index) => (
              <Button type="link" danger onClick={() => removeHoliday(index)}>
                Remove
              </Button>
            ),
          },
        ]}
      />
    </Card>
  )
}

export default InactivityTenantSettings
